import {
  Align,
  CHANNEL_BIO_MAX,
  CHANNEL_DISPLAY_NAME_MAX,
  DEFAULT_SWARM_API_URL,
  GO_LIVE_NOTIFICATION_MAX,
  PROJECT_SCHEMA_VERSION,
  SWARM_DEFAULTS_REVISION,
  STREAM_CATEGORY_MAX,
  STREAM_TITLE_MAX,
  type AudioStrip,
  type FilterInstance,
  type ItemTransform,
  type Project,
  type SceneItem,
  type SettingsBag,
  type TransitionConfig,
} from '../core/model';
import {
  cleanAccentColor,
  cleanContentLabels,
  cleanFeatured,
  cleanClips,
  cleanGoal,
  cleanRetentionDays,
  cleanRaids,
  cleanFavourites,
  cleanLanguage,
  cleanLinks,
  cleanMultilineText,
  cleanPortalUrl,
  cleanSchedule,
  cleanTags,
  cleanText,
  httpsUrl,
} from '../core/channel';
import { cleanCaptions } from '../core/captions';
import { pinSystemItems } from '../core/system-items';
import type { ProjectMigrator } from './api';

type Raw = Record<string, unknown>;

const BLEND_MODES = ['normal', 'additive', 'subtract', 'screen', 'multiply', 'lighten', 'darken'] as const;
const SCALE_FILTERS = ['default', 'point', 'bilinear', 'bicubic', 'lanczos', 'area'] as const;
const BOUNDS_TYPES = [
  'none',
  'stretch',
  'scale-inner',
  'scale-outer',
  'scale-to-width',
  'scale-to-height',
  'max-only',
] as const;
const MONITORING = ['off', 'monitor-only', 'monitor-and-output'] as const;
const CODECS = ['auto', 'vp8', 'vp9', 'h264', 'av1'] as const;
const DEGRADATION = ['balanced', 'maintain-framerate', 'maintain-resolution'] as const;

const MAX_GROUP_DEPTH = 32;

function isObject(v: unknown): v is Raw {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function num(v: unknown, fallback: number, min = -Infinity, max = Infinity): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : fallback;
}

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

function bool(v: unknown, fallback: boolean): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

function oneOf<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  return typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
}

const ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
const RESERVED_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/** Ids become dictionary keys, so inherited names and prototype pollution vectors are never valid. */
function isValidId(v: unknown): v is string {
  return typeof v === 'string' && ID_PATTERN.test(v) && !RESERVED_KEYS.has(v);
}

/** Looser check for keys that are not generated ids (action ids, plugin ids like "com.x.y"). */
function isSafeKey(v: string): boolean {
  return !RESERVED_KEYS.has(v);
}

const has = (o: object, k: string): boolean => Object.hasOwn(o, k);

function newId(): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 16);
}

/** Settings bags are opaque JSON owned by plugins/sources; copy so the result never aliases the input. */
function settingsBag(v: unknown): SettingsBag {
  if (!isObject(v)) return {};
  try {
    return structuredClone(v);
  } catch {
    return {};
  }
}

// ---------------------------------------------------------------------------
// Version ladder. migrations[n] upgrades a document at schemaVersion n to n + 1
// and must only reshape data; validation and repair run afterwards on the
// current shape. A document without a schemaVersion is treated as version 0.
// ---------------------------------------------------------------------------

type Step = (doc: Raw) => Raw;

export const MIGRATIONS: Readonly<Record<number, Step>> = {
  // v0 predates plugins, hotkeys and the scene order list (scene order was the key order of `scenes`).
  0: (doc) => {
    const next: Raw = { ...doc };
    if (!isObject(next.scenes)) throw new Error('Project has no scenes');
    if (!Array.isArray(next.sceneOrder)) next.sceneOrder = Object.keys(next.scenes);
    if (!Array.isArray(next.transitionOrder) && isObject(next.transitions)) {
      next.transitionOrder = Object.keys(next.transitions);
    }
    if (!isObject(next.hotkeys)) next.hotkeys = {};
    if (!Array.isArray(next.plugins)) next.plugins = [];
    if (!isObject(next.pluginData)) next.pluginData = {};
    if (next.transitionDurationMs === undefined) next.transitionDurationMs = 300;
    next.schemaVersion = 1;
    return next;
  },
  // v2 adds the optional per-source `settingsVersion` and `runtimeState`. Older sources have
  // neither: absent means "settings never migrated" and "no saved run state".
  1: (doc) => ({ ...doc, schemaVersion: 2 }),
  // v3 adds `channel` (stream info and the public profile). Absent means "nothing set": repair fills the defaults.
  2: (doc) => ({ ...doc, schemaVersion: 3 }),
};

function upgrade(input: Raw): Raw {
  let version = input.schemaVersion === undefined ? 0 : input.schemaVersion;
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 0) {
    throw new Error(`Project has an invalid schemaVersion (${String(version)})`);
  }
  if (version > PROJECT_SCHEMA_VERSION) {
    throw new Error(
      `Project was saved by a newer version of the app (schema ${version}, this build understands up to ${PROJECT_SCHEMA_VERSION})`,
    );
  }
  let doc = input;
  while (version < PROJECT_SCHEMA_VERSION) {
    const step = MIGRATIONS[version];
    if (!step) throw new Error(`No migration from schema version ${version}`);
    doc = step(doc);
    version += 1;
  }
  return doc;
}

// ---------------------------------------------------------------------------
// Validation and repair of the current shape.
// ---------------------------------------------------------------------------

function repairTransform(raw: unknown): ItemTransform {
  const t = isObject(raw) ? raw : {};
  const vec = (v: unknown, fx: number, fy: number) => {
    const o = isObject(v) ? v : {};
    return { x: num(o.x, fx), y: num(o.y, fy) };
  };
  const crop = isObject(t.crop) ? t.crop : {};
  return {
    position: vec(t.position, 0, 0),
    scale: vec(t.scale, 1, 1),
    rotation: num(t.rotation, 0),
    alignment: Math.trunc(num(t.alignment, Align.LEFT | Align.TOP, 0, 15)),
    boundsType: oneOf(t.boundsType, BOUNDS_TYPES, 'none'),
    boundsAlignment: Math.trunc(num(t.boundsAlignment, Align.CENTER, 0, 15)),
    bounds: vec(t.bounds, 0, 0),
    crop: {
      left: num(crop.left, 0, 0),
      top: num(crop.top, 0, 0),
      right: num(crop.right, 0, 0),
      bottom: num(crop.bottom, 0, 0),
    },
  };
}

function repairFilters(raw: unknown): FilterInstance[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: FilterInstance[] = [];
  for (const f of raw) {
    if (!isObject(f) || typeof f.kind !== 'string' || f.kind === '') continue;
    let id = isValidId(f.id) ? f.id : newId();
    if (seen.has(id)) id = newId();
    seen.add(id);
    out.push({
      id,
      kind: f.kind,
      name: str(f.name, f.kind),
      enabled: bool(f.enabled, true),
      settings: settingsBag(f.settings),
    });
  }
  return out;
}

function repairAudioStrip(raw: unknown): AudioStrip {
  const a = isObject(raw) ? raw : {};
  return {
    volume: num(a.volume, 1, 0),
    muted: bool(a.muted, false),
    balance: num(a.balance, 0, -1, 1),
    forceMono: bool(a.forceMono, false),
    syncOffsetMs: num(a.syncOffsetMs, 0, 0),
    monitoring: oneOf(a.monitoring, MONITORING, 'off'),
  };
}

interface Context {
  sourceIds: ReadonlySet<string>;
  sceneIds: ReadonlySet<string>;
  usedItemIds: Set<string>;
}

function repairItem(raw: unknown, ctx: Context, depth: number): SceneItem | null {
  if (!isObject(raw)) return null;
  let id = isValidId(raw.id) ? raw.id : newId();
  if (ctx.usedItemIds.has(id)) id = newId();
  const base = {
    id,
    visible: bool(raw.visible, true),
    locked: bool(raw.locked, false),
    transform: repairTransform(raw.transform),
    blendMode: oneOf(raw.blendMode, BLEND_MODES, 'normal'),
    scaleFilter: oneOf(raw.scaleFilter, SCALE_FILTERS, 'default'),
    opacity: num(raw.opacity, 1, 0, 1),
  };
  switch (raw.type) {
    case 'source':
      if (!isValidId(raw.sourceId) || !ctx.sourceIds.has(raw.sourceId)) return null;
      ctx.usedItemIds.add(id);
      return { ...base, type: 'source', sourceId: raw.sourceId };
    case 'scene':
      if (!isValidId(raw.sceneId) || !ctx.sceneIds.has(raw.sceneId)) return null;
      ctx.usedItemIds.add(id);
      return { ...base, type: 'scene', sceneId: raw.sceneId };
    case 'group': {
      if (depth >= MAX_GROUP_DEPTH) return null;
      ctx.usedItemIds.add(id);
      const children = Array.isArray(raw.children) ? raw.children : [];
      return {
        ...base,
        type: 'group',
        name: str(raw.name, 'Group'),
        children: children.flatMap((c) => repairItem(c, ctx, depth + 1) ?? []),
      };
    }
    default:
      return null;
  }
}

function nestedSceneIds(items: readonly SceneItem[], out: string[] = []): string[] {
  for (const item of items) {
    if (item.type === 'scene') out.push(item.sceneId);
    else if (item.type === 'group') nestedSceneIds(item.children, out);
  }
  return out;
}

function reaches(scenes: Project['scenes'], from: string, target: string, seen = new Set<string>()): boolean {
  if (from === target) return true;
  if (seen.has(from)) return false;
  seen.add(from);
  return nestedSceneIds(scenes[from]?.items ?? []).some((n) => reaches(scenes, n, target, seen));
}

/** Drops nested-scene items that would close a cycle. Edges are only ever removed, so one pass suffices. */
function breakSceneCycles(scenes: Project['scenes']): void {
  for (const scene of Object.values(scenes)) {
    const strip = (items: SceneItem[]): SceneItem[] =>
      items.flatMap<SceneItem>((item) => {
        if (item.type === 'scene') return reaches(scenes, item.sceneId, scene.id) ? [] : [item];
        if (item.type === 'group') return [{ ...item, children: strip(item.children) }];
        return [item];
      });
    scene.items = strip(scene.items);
  }
}

function repairIceServers(raw: unknown, fallback: Project['output']['streaming']['iceServers']) {
  if (!Array.isArray(raw)) return fallback;
  const out: Project['output']['streaming']['iceServers'] = [];
  for (const s of raw) {
    if (!isObject(s)) continue;
    const urls = s.urls;
    const validUrls =
      typeof urls === 'string' ? urls !== '' : Array.isArray(urls) && urls.length > 0 && urls.every((u) => typeof u === 'string' && u !== '');
    if (!validUrls) continue;
    const entry: Project['output']['streaming']['iceServers'][number] = { urls: urls as string | string[] };
    if (typeof s.username === 'string') entry.username = s.username;
    if (typeof s.credential === 'string') entry.credential = s.credential;
    out.push(entry);
  }
  return out;
}

function repairOutput(raw: unknown): Project['output'] {
  const o = isObject(raw) ? raw : {};
  const s = isObject(o.streaming) ? o.streaming : {};
  const r = isObject(o.recording) ? o.recording : {};
  return {
    streaming: {
      mode: oneOf(s.mode, ['p2p', 'whip', 'swarm'] as const, 'p2p'),
      signalingUrl: str(s.signalingUrl, 'ws://localhost:8787'),
      iceServers: repairIceServers(s.iceServers, [{ urls: 'stun:stun.l.google.com:19302' }]),
      iceTransportPolicy: s.iceTransportPolicy === 'relay' ? 'relay' : 'all',
      maxViewers: Math.trunc(num(s.maxViewers, 8, 1, 1000)),
      allowGuests: bool(s.allowGuests, false),
      guestLink: bool(s.guestLink, false),
      guestApproval: oneOf(s.guestApproval, ['lobby', 'auto'] as const, 'lobby'),
      videoKbps: num(s.videoKbps, 4500, 1),
      audioKbps: num(s.audioKbps, 128, 1),
      codec: oneOf(s.codec, CODECS, 'auto'),
      degradation: oneOf(s.degradation, DEGRADATION, 'balanced'),
      whipEndpoint: str(s.whipEndpoint, ''),
      whipBearerToken: str(s.whipBearerToken, ''),
      swarmApiUrl: str(s.swarmApiUrl, DEFAULT_SWARM_API_URL),
      swarmStreamKey: str(s.swarmStreamKey, ''),
      // Before revision 1 the default was 1 s; it moves to the 200 ms delivery profile. 2 s was a choice and stays.
      swarmSegmentMs: s.swarmSegmentMs === 2000 ? 2000
        : s.swarmSegmentMs === 1000 && num(s.swarmDefaultsRevision, 0, 0) >= SWARM_DEFAULTS_REVISION ? 1000
        : 200,
      swarmDefaultsRevision: SWARM_DEFAULTS_REVISION,
      swarmH264Fallback: s.swarmH264Fallback === 'off' ? 'off' : 'auto',
      captions: cleanCaptions(s.captions),
    },
    recording: {
      mimeType: str(r.mimeType, 'auto'),
      videoKbps: num(r.videoKbps, 8000, 1),
      audioKbps: num(r.audioKbps, 160, 1),
      sink: oneOf(r.sink, ['memory', 'opfs'] as const, 'opfs'),
    },
  };
}

function repairChannel(raw: unknown): Project['channel'] {
  const c = isObject(raw) ? raw : {};
  const info = isObject(c.info) ? c.info : {};
  const profile = isObject(c.profile) ? c.profile : {};
  return {
    info: {
      // Never restored: the title is the channel's, and the project only holds one typed in the running session until it is published.
      title: '',
      goLiveNotification: cleanText(info.goLiveNotification, GO_LIVE_NOTIFICATION_MAX),
      category: cleanText(info.category, STREAM_CATEGORY_MAX),
      tags: cleanTags(info.tags),
      language: cleanLanguage(info.language),
      contentLabels: cleanContentLabels(info.contentLabels),
      rerun: bool(info.rerun, false),
    },
    profile: {
      displayName: cleanText(profile.displayName, CHANNEL_DISPLAY_NAME_MAX),
      bio: cleanMultilineText(profile.bio, CHANNEL_BIO_MAX),
      links: cleanLinks(profile.links),
      accentColor: cleanAccentColor(profile.accentColor),
      avatarUrl: httpsUrl(profile.avatarUrl),
      bannerUrl: httpsUrl(profile.bannerUrl),
      offlineImageUrl: httpsUrl(profile.offlineImageUrl),
    },
    schedule: cleanSchedule(c.schedule),
    featured: cleanFeatured(c.featured),
    goal: cleanGoal(c.goal),
    raids: cleanRaids(c.raids),
    clips: cleanClips(c.clips),
    favourites: cleanFavourites(c.favourites),
    vodRetentionDays: cleanRetentionDays(c.vodRetentionDays),
    saveBroadcasts: bool(c.saveBroadcasts, false),
    disconnectProtection: bool(c.disconnectProtection, false),
    portalUrl: cleanPortalUrl(c.portalUrl),
  };
}

function repairTransitions(
  raw: unknown,
  orderRaw: unknown,
  currentRaw: unknown,
): Pick<Project, 'transitions' | 'transitionOrder' | 'currentTransitionId'> {
  const transitions: Record<string, TransitionConfig> = {};
  if (isObject(raw)) {
    for (const [key, t] of Object.entries(raw)) {
      if (!isValidId(key) || !isObject(t) || typeof t.kind !== 'string' || t.kind === '') continue;
      transitions[key] = { id: key, kind: t.kind, name: str(t.name, t.kind), settings: settingsBag(t.settings) };
    }
  }
  if (Object.keys(transitions).length === 0) {
    const cut = newId();
    const fade = newId();
    transitions[cut] = { id: cut, kind: 'cut', name: 'Cut', settings: {} };
    transitions[fade] = { id: fade, kind: 'fade', name: 'Fade', settings: {} };
  }
  const order = orderFor(transitions, orderRaw);
  const current =
    isValidId(currentRaw) && has(transitions, currentRaw)
      ? currentRaw
      : (order.find((id) => transitions[id]!.kind === 'fade') ?? order[0]!);
  return { transitions, transitionOrder: order, currentTransitionId: current };
}

function orderFor(record: Record<string, unknown>, orderRaw: unknown): string[] {
  const seen = new Set<string>();
  const order: string[] = [];
  if (Array.isArray(orderRaw)) {
    for (const id of orderRaw) {
      if (isValidId(id) && has(record, id) && !seen.has(id)) {
        seen.add(id);
        order.push(id);
      }
    }
  }
  for (const id of Object.keys(record)) {
    if (!seen.has(id)) order.push(id);
  }
  return order;
}

function repair(doc: Raw): Project {
  if (typeof doc.id !== 'string' || doc.id === '') throw new Error('Project is missing an id');
  if (!isObject(doc.scenes)) throw new Error('Project has no scenes');
  if (doc.sources !== undefined && !isObject(doc.sources)) throw new Error('Project sources are malformed');

  const now = Date.now();
  const video = isObject(doc.video) ? doc.video : {};
  const audio = isObject(doc.audio) ? doc.audio : {};

  const sources: Project['sources'] = {};
  for (const [key, s] of Object.entries(isObject(doc.sources) ? doc.sources : {})) {
    if (!isValidId(key) || !isObject(s) || typeof s.kind !== 'string' || s.kind === '') continue;
    const source: Project['sources'][string] = {
      id: key,
      name: str(s.name, s.kind),
      kind: s.kind,
      settings: settingsBag(s.settings),
      filters: repairFilters(s.filters),
      audio: repairAudioStrip(s.audio),
    };
    if (typeof s.settingsVersion === 'number' && Number.isInteger(s.settingsVersion) && s.settingsVersion >= 0) {
      source.settingsVersion = s.settingsVersion;
    }
    if (isObject(s.runtimeState)) source.runtimeState = settingsBag(s.runtimeState);
    sources[key] = source;
  }

  const sceneKeys = Object.entries(doc.scenes)
    .filter(([k, s]) => isValidId(k) && isObject(s))
    .map(([k]) => k);
  const ctx: Context = {
    sourceIds: new Set(Object.keys(sources)),
    sceneIds: new Set(sceneKeys),
    usedItemIds: new Set(),
  };
  const scenes: Project['scenes'] = {};
  for (const key of sceneKeys) {
    const s = doc.scenes[key] as Raw;
    scenes[key] = {
      id: key,
      name: str(s.name, 'Scene'),
      items: (Array.isArray(s.items) ? s.items : []).flatMap((i) => repairItem(i, ctx, 0) ?? []),
      filters: repairFilters(s.filters),
    };
  }
  if (Object.keys(scenes).length === 0) {
    const id = newId();
    scenes[id] = { id, name: 'Scene', items: [], filters: [] };
  }
  breakSceneCycles(scenes);
  // Imported or older projects may hold a system layer hidden, grouped or below others.
  pinSystemItems({ sources, scenes });

  const sceneOrder = orderFor(scenes, doc.sceneOrder);
  const currentSceneId =
    isValidId(doc.currentSceneId) && has(scenes, doc.currentSceneId) ? doc.currentSceneId : sceneOrder[0]!;

  const hotkeys: Project['hotkeys'] = {};
  if (isObject(doc.hotkeys)) {
    for (const [action, chords] of Object.entries(doc.hotkeys)) {
      if (isSafeKey(action) && Array.isArray(chords)) hotkeys[action] = chords.filter((c): c is string => typeof c === 'string');
    }
  }

  const plugins: Project['plugins'] = [];
  const pluginIds = new Set<string>();
  for (const p of Array.isArray(doc.plugins) ? doc.plugins : []) {
    if (!isObject(p) || typeof p.id !== 'string' || p.id === '' || typeof p.url !== 'string' || p.url === '') continue;
    if (pluginIds.has(p.id)) continue;
    pluginIds.add(p.id);
    plugins.push({ id: p.id, url: p.url, enabled: bool(p.enabled, true) });
  }

  const pluginData: Project['pluginData'] = {};
  if (isObject(doc.pluginData)) {
    for (const [pid, data] of Object.entries(doc.pluginData)) {
      if (isSafeKey(pid) && isObject(data)) pluginData[pid] = settingsBag(data);
    }
  }

  const createdAt = num(doc.createdAt, now);
  return {
    schemaVersion: PROJECT_SCHEMA_VERSION,
    id: doc.id,
    name: str(doc.name, 'Untitled'),
    createdAt,
    updatedAt: num(doc.updatedAt, createdAt),
    video: {
      width: Math.trunc(num(video.width, 1920, 1, 16384)),
      height: Math.trunc(num(video.height, 1080, 1, 16384)),
      fps: num(video.fps, 30, 1, 240),
    },
    audio: {
      sampleRate: audio.sampleRate === 44100 ? 44100 : 48000,
      masterVolume: num(audio.masterVolume, 1, 0),
      masterMuted: bool(audio.masterMuted, false),
      monitorDeviceId: typeof audio.monitorDeviceId === 'string' ? audio.monitorDeviceId : null,
    },
    output: repairOutput(doc.output),
    channel: repairChannel(doc.channel),
    sources,
    scenes,
    sceneOrder,
    currentSceneId,
    ...repairTransitions(doc.transitions, doc.transitionOrder, doc.currentTransitionId),
    transitionDurationMs: num(doc.transitionDurationMs, 300, 0),
    hotkeys,
    plugins,
    pluginData,
  };
}

export const migrateProject: ProjectMigrator = (raw) => {
  if (!isObject(raw)) throw new Error('Project data must be an object');
  return repair(upgrade(raw));
};
